import {asQueryString} from '@wallaby-ui/core';
import {
  BreadcrumbItem,
  formatDocumentBreadcrumb,
  formatEncounterBreadcrumb,
  formatPatientBreadcrumb,
  HaidiSchema,
  LightBreadcrumbs,
  useFormatters,
  useStableDocumentBreadcrumb,
} from '@wallaby-ui/document';
import React from 'react';
import {Helmet} from 'react-helmet';
import {useIntl} from 'react-intl';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {HospitalizationMedbasePortalLinkButton} from './HospitalizationMedbasePortalLinkButton';

type EncounterEventType = HaidiSchema.components['schemas']['EncounterPropertiesDto']['encounterType'];
type IconDef = {
  iconName: string;
  iconViewBox: string;
};
export type HospitalizationPatientProp = {
  isLoading: boolean;
  id: string | number | undefined;
  name: string | undefined;
  birthNumber: string | undefined;
  srcId: string | undefined;
};
export type HospitalizationEncounterProp = {
  isLoading: boolean;
  id: string | undefined;
  encounterType: EncounterEventType | undefined;
  orgShortName: string | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  isDeleted: boolean | undefined;
  isEscort: boolean | undefined;
};
export type HospitalizationDocumentProp = {
  isLoading: boolean;
  id: string | undefined;
  name: string | undefined;
  timestamp: string | undefined;
};
export type HospitalizationBreadcrumbProps = {
  patient: HospitalizationPatientProp;
  encounter?: HospitalizationEncounterProp;
  document?: HospitalizationDocumentProp;
  state: HospitalizationState;
};
export const HospitalizationBreadcrumbs: React.FC<HospitalizationBreadcrumbProps> = (props) => {
  const {patient, encounter, state: _} = props;
  const document = useStableDocumentBreadcrumb(props.document);
  const formatters = useFormatters();
  const intl = useIntl();
  const items: BreadcrumbItem[] = [];
  let patientLabel = '';
  if (patient?.id && patient?.name) {
    patientLabel = formatPatientBreadcrumb(patient, intl);
    items.push({
      iconName: 'haidiUserIconMultiColor',
      iconViewBox: '0 0 16 16',
      text: patientLabel,
      url: '', // TODO: allow click when patient page is implemented
    });
  }
  // encounter
  const ESCORT_ENCOUNTER_COLORS = {
    primaryColor: '#FFDACA',
    secondaryColor: '#FF8717',
  };
  const DELETED_ENCOUNTER_COLORS = {
    primaryColor: '#FFD6D6',
    secondaryColor: '#FF5353',
  };
  const encounterParams: Partial<HospitalizationState> = {};
  let encounterLabel = '';
  if (encounter?.id) {
    encounterLabel = formatEncounterBreadcrumb(encounter, formatters);
    const iconMap: Record<EncounterEventType, IconDef> = {
      OUTPATIENT: {iconName: 'haidiStethoscopeIcon', iconViewBox: '0 0 12 12'},
      HOSPITALIZATION: {
        iconName: 'haidiBedIconMultiColor',
        iconViewBox: '0 0 17 12',
      },
    };
    items.push({
      ...(iconMap?.[encounter.encounterType ?? 'HOSPITALIZATION'] ?? iconMap.HOSPITALIZATION),
      ...(encounter.isEscort ? ESCORT_ENCOUNTER_COLORS : {}),
      ...(encounter.isDeleted ? DELETED_ENCOUNTER_COLORS : {}),
      text: encounterLabel,
      url: `/hospitalizedPatients/v2/${encounter.id}?${asQueryString(encounterParams)}`,
    });
  }
  // document
  let documentLabel = '';
  if (document?.id && document?.name) {
    documentLabel = formatDocumentBreadcrumb(document, formatters);
    items.push({
      ...(encounter?.id
        ? {
            iconName: 'haidiDocumentIconMultiColor2',
            iconViewBox: '0 0 17 16',
            url: `/hospitalizedPatients/v2/${encounter.id}/document/${document.id}`,
          }
        : {
            iconName: 'haidiLabIconMultiColor',
            iconViewBox: '0 0 16 16',
            url: `/hospitalizedPatients/v2/undefined/document/${document.id}`,
          }),
      text: documentLabel,
    });
  }
  return (
    <div css={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
      <Helmet title={[patientLabel, documentLabel || encounterLabel].join(' | ')} />
      <LightBreadcrumbs items={items} style={{marginBottom: 0, padding: 0}} />
      <HospitalizationMedbasePortalLinkButton patientSrcId={patient.srcId} />
    </div>
  );
};
